document.addEventListener('DOMContentLoaded', () => {
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const animatedTargets = new Set();
	const accordionRevealTimers = new WeakMap();
	let scrollObserver;

	const getScopedElements = (root, selector) => {
		const matches = [];

		if (root instanceof Element && root.matches(selector)) {
			matches.push(root);
		}

		return matches.concat(Array.from(root.querySelectorAll(selector)));
	};

	const registerAnimatedTarget = (element, className, delay) => {
		if (!element) {
			return;
		}

		element.classList.add(className);

		if (typeof delay === 'number' && !Number.isNaN(delay) && !element.style.getPropertyValue('--scroll-delay')) {
			element.style.setProperty('--scroll-delay', `${delay}ms`);
		}

		animatedTargets.add(element);
	};

	const prepareScrollGroups = (root = document) => {
		getScopedElements(root, '[data-scroll-group]').forEach((group) => {
			if (group.dataset.scrollGroupPrepared === 'true') {
				return;
			}

			const staggerStep = Number.parseInt(group.dataset.scrollStagger || '110', 10);
			const groupChildren = Array.from(group.children).filter((child) => child.nodeType === Node.ELEMENT_NODE);
			const childSelector = group.dataset.scrollGroupChildSelector;
			const groupTargets = childSelector
				? groupChildren
					.map((child) => {
						if (child.matches(childSelector)) {
							return child;
						}

						return child.querySelector(childSelector);
					})
					.filter(Boolean)
				: groupChildren;

			groupTargets.forEach((target, index) => {
				registerAnimatedTarget(target, 'scroll-reveal', index * staggerStep);
			});

			group.dataset.scrollGroupPrepared = 'true';
		});
	};

	const prepareFadeTargets = (root = document) => {
		getScopedElements(root, '[data-scroll-fade]').forEach((element) => {
			if (element.dataset.scrollFadePrepared === 'true') {
				return;
			}

			const delay = Number.parseInt(element.dataset.scrollDelay || '', 10);

			registerAnimatedTarget(element, 'scroll-fade', delay);

			element.dataset.scrollFadePrepared = 'true';
		});
	};

	const prepareBackgroundSectionTargets = (root = document) => {
		getScopedElements(root, '[data-scroll-bg-section]').forEach((element) => {
			if (element.dataset.scrollBgPrepared === 'true') {
				return;
			}

			const delay = Number.parseInt(element.dataset.scrollDelay || '', 10);

			registerAnimatedTarget(element, 'scroll-bg-section', delay);

			element.dataset.scrollBgPrepared = 'true';
		});
	};

	const prepareScrollAnimations = (root = document) => {
		prepareScrollGroups(root);
		prepareFadeTargets(root);
		prepareBackgroundSectionTargets(root);
	};

	const elementIsInView = (element) => {
		const rect = element.getBoundingClientRect();
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
		const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

		return rect.width > 0
			&& rect.height > 0
			&& rect.bottom >= viewportHeight * 0.08
			&& rect.top <= viewportHeight * 0.92
			&& rect.right >= 0
			&& rect.left <= viewportWidth;
	};

	const isJourneyContentTarget = (element) => element.closest('[data-journey-content]') !== null;

	const parseTimeValue = (value) => {
		const time = value.trim();

		if (!time) {
			return 0;
		}

		return time.endsWith('ms') ? Number.parseFloat(time) : Number.parseFloat(time) * 1000;
	};

	const getTransitionDuration = (element) => {
		const computedStyles = window.getComputedStyle(element);
		const durations = computedStyles.transitionDuration.split(',').map(parseTimeValue);
		const delays = computedStyles.transitionDelay.split(',').map(parseTimeValue);

		return durations.reduce((maxDuration, duration, index) => {
			const delay = delays[index] ?? delays[0] ?? 0;

			return Math.max(maxDuration, duration + delay);
		}, 0);
	};

	const resetJourneyContentAnimation = (content) => {
		const timerId = accordionRevealTimers.get(content);

		if (timerId) {
			window.clearTimeout(timerId);
			accordionRevealTimers.delete(content);
		}

		content.querySelectorAll('.scroll-reveal').forEach((element) => {
			element.classList.remove('is-inview');

			if (scrollObserver) {
				scrollObserver.unobserve(element);
			}
		});
	};

	const playJourneyContentAnimation = (item, content) => {
		const revealTargets = Array.from(content.querySelectorAll('.scroll-reveal'));

		if (!revealTargets.length) {
			return;
		}

		resetJourneyContentAnimation(content);

		if (prefersReducedMotion) {
			revealTargets.forEach((element) => element.classList.add('is-inview'));
			return;
		}

		const revealDelay = Math.max(180, getTransitionDuration(item));
		const timerId = window.setTimeout(() => {
			revealTargets.forEach((element) => {
				element.classList.add('is-inview');
			});

			accordionRevealTimers.delete(content);
		}, revealDelay);

		accordionRevealTimers.set(content, timerId);
	};

	const revealTarget = (element) => {
		element.classList.add('is-inview');

		if (scrollObserver) {
			scrollObserver.unobserve(element);
		}
	};

	const observeScrollAnimations = () => {
		if (prefersReducedMotion) {
			animatedTargets.forEach((target) => target.classList.add('is-inview'));
			return;
		}

		if (!scrollObserver) {
			scrollObserver = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						revealTarget(entry.target);
					}
				});
			}, {
				threshold: 0.18,
				rootMargin: '0px 0px -10% 0px',
			});
		}

			animatedTargets.forEach((target) => {
				if (target.classList.contains('is-inview')) {
					return;
				}

				if (isJourneyContentTarget(target)) {
					return;
				}

				if (elementIsInView(target)) {
					revealTarget(target);
					return;
				}

				scrollObserver.observe(target);
			});
	};

	const initialiseScrollAnimations = () => {
		prepareScrollAnimations();

		if (prefersReducedMotion) {
			animatedTargets.forEach((target) => target.classList.add('is-inview'));
			return;
		}

		animatedTargets.forEach((target) => {
			if (isJourneyContentTarget(target)) {
				return;
			}

			if (elementIsInView(target)) {
				target.classList.add('is-inview');
			}
		});

		document.documentElement.classList.add('has-scroll-animations');
		observeScrollAnimations();
	};

	const initialisePageLoadAnimations = () => {
		const pageLoadTargets = document.querySelectorAll('[data-page-load-delay]');
		const pageLoadNav = document.querySelector('[data-page-load-nav]');

		pageLoadTargets.forEach((target) => {
			const delay = Number.parseInt(target.dataset.pageLoadDelay || '0', 10);

			target.style.setProperty('--page-load-delay', `${delay}ms`);
		});

		if (pageLoadNav) {
			const toggler = pageLoadNav.querySelector('.navbar-toggler');
			const navLinks = pageLoadNav.querySelectorAll('#main-menu > .menu-item > .nav-link');

			if (toggler) {
				toggler.style.setProperty('--page-load-delay', '120ms');
			}

			navLinks.forEach((link, index) => {
				const delay = `${220 + (index * 90)}ms`;
				const menuItem = link.closest('.menu-item');

				link.style.setProperty('--page-load-delay', delay);

				if (menuItem) {
					menuItem.style.setProperty('--page-load-delay', delay);
				}
			});
		}

		const playPageLoadAnimation = () => {
			document.documentElement.classList.add('has-page-load-complete');
		};

		if (prefersReducedMotion) {
			playPageLoadAnimation();
			return;
		}

		if (document.readyState === 'complete') {
			window.requestAnimationFrame(() => {
				window.setTimeout(playPageLoadAnimation, 60);
			});
			return;
		}

		window.addEventListener('load', () => {
			window.requestAnimationFrame(() => {
				window.setTimeout(playPageLoadAnimation, 60);
			});
		}, { once: true });
	};

	const refreshScrollAnimations = (root = document) => {
		prepareScrollAnimations(root);

		if (prefersReducedMotion) {
			animatedTargets.forEach((target) => target.classList.add('is-inview'));
			return;
		}

		window.requestAnimationFrame(() => {
			observeScrollAnimations();
		});
	};

	const initialiseHeroLogoSwap = () => {
		const heroLogo = document.querySelector('.hero-split-section__logo');
		const headerLogo = document.querySelector('.ensora-header-logo-link.is-hidden-by-hero');
		const mainNav = document.getElementById('main-nav');

		if (!heroLogo || !headerLogo || !mainNav) {
			return;
		}

		let ticking = false;

		const updateHeroLogoSwap = () => {
			ticking = false;

			const heroLogoRect = heroLogo.getBoundingClientRect();
			const navRect = mainNav.getBoundingClientRect();
			const shouldShowHeaderLogo = heroLogoRect.bottom <= navRect.bottom + 8;

			headerLogo.classList.toggle('is-visible', shouldShowHeaderLogo);
		};

		const requestHeroLogoSwapUpdate = () => {
			if (ticking) {
				return;
			}

			ticking = true;

			window.requestAnimationFrame(updateHeroLogoSwap);
		};

		requestHeroLogoSwapUpdate();
		window.addEventListener('scroll', requestHeroLogoSwapUpdate, { passive: true });
		window.addEventListener('resize', requestHeroLogoSwapUpdate);
		window.addEventListener('load', requestHeroLogoSwapUpdate, { once: true });
	};

	const prepareTestimonialsSlideAnimation = (slide) => {
		if (prefersReducedMotion || !slide) {
			return;
		}

		const animatedContent = slide.querySelector('.testimonials-slide__content');

		if (!animatedContent) {
			return;
		}

		animatedContent.classList.remove('is-replaying');
		animatedContent.classList.add('is-preparing');
	};

	const replayTestimonialsSlideAnimation = (slide) => {
		if (prefersReducedMotion || !slide) {
			return;
		}

		const animatedContent = slide.querySelector('.testimonials-slide__content');

		if (!animatedContent) {
			return;
		}

		animatedContent.classList.remove('is-replaying');
		animatedContent.classList.remove('is-preparing');
		void animatedContent.offsetWidth;
		animatedContent.classList.add('is-replaying');
	};

	const accordions = document.querySelectorAll('.js-journey-accordion');

	accordions.forEach((accordion) => {
		const items = accordion.querySelectorAll('[data-journey-item]');

		const closeAllItems = () => {
			accordion.classList.remove('has-active');

			items.forEach((item) => {
				const trigger = item.querySelector('[data-journey-trigger]');
				const content = item.querySelector('[data-journey-content]');

				item.classList.remove('is-active');

				if (trigger) {
					trigger.setAttribute('aria-expanded', 'false');
				}

					if (content) {
						resetJourneyContentAnimation(content);
						content.hidden = true;
					}
				});
		};

		closeAllItems();

		items.forEach((item) => {
			const trigger = item.querySelector('[data-journey-trigger]');
			const content = item.querySelector('[data-journey-content]');

			if (!trigger || !content) {
				return;
			}

			trigger.addEventListener('click', () => {
				const isActive = item.classList.contains('is-active');

				closeAllItems();

					if (isActive) {
						return;
					}

					accordion.classList.add('has-active');
					item.classList.add('is-active');
					trigger.setAttribute('aria-expanded', 'true');
					content.hidden = false;

					refreshScrollAnimations(content);
					playJourneyContentAnimation(item, content);
				});
			});
		});

	initialiseScrollAnimations();
	initialisePageLoadAnimations();
	initialiseHeroLogoSwap();

	const testimonialsCarousel = document.getElementById('testimonials-carousel');

	if (testimonialsCarousel) {
		const initialActiveSlide = testimonialsCarousel.querySelector('.carousel-item.active');

		prepareTestimonialsSlideAnimation(initialActiveSlide);

		window.requestAnimationFrame(() => {
			replayTestimonialsSlideAnimation(initialActiveSlide);
		});

		testimonialsCarousel.addEventListener('slide.bs.carousel', (event) => {
			prepareTestimonialsSlideAnimation(event.relatedTarget);
		});
	}

	document.addEventListener('shown.bs.tab', (event) => {
		const paneSelector = event.target.getAttribute('data-bs-target') || event.target.getAttribute('href');
		const pane = paneSelector ? document.querySelector(paneSelector) : null;

		refreshScrollAnimations(pane || document);
	});

	document.addEventListener('slid.bs.carousel', (event) => {
		const activeItem = event.target instanceof Element ? event.target.querySelector('.carousel-item.active') : null;

		refreshScrollAnimations(activeItem || document);

		if (event.target instanceof Element && event.target.id === 'testimonials-carousel') {
			replayTestimonialsSlideAnimation(activeItem);
		}
	});
});
